import React, { FC, useEffect, useState } from 'react';

import { Clients } from '@ariksa/notification';
import { AlertResponse, NotificationFor } from '@ariksa/notification/api';
import {
  Box,
  Checkbox,
  HStack,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { each, filter, includes, isEmpty, toArray } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';

import {
  renderAlertCategory,
  renderSeverityBar,
  renderTime,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import {
  ComplianceCheckPassedIcon,
  PageInfo,
  RemediateIcon,
  TableActionsMenu,
  IconTypes,
  PrimaryButton,
  ExceptionIcon,
  Select,
  SeverityIcon,
  NotifyIcon,
  TicketIcon,
} from 'components/index';
import { ExcludePoliciesForResource } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Alerts/ExcludePoliciesForResources';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import {
  renderAlertsStatus,
  renderAlertsStatusHeader,
} from 'containers/Findings/AlertDrawer/Components/Aggregated/getAlertColumns';
import { RemediationModal } from 'containers/Findings/AlertDrawer/Components/Individual/Remediation/RemediationModal';
import { useAlertStatus } from 'containers/Findings/AlertDrawer/hooks/useAlertStatus';
import { AddException } from 'containers/Findings/Alerts/Components/AddException';
import { CreateNotification } from 'containers/Findings/Alerts/Components/CreateNotification';
import { CreateTicket } from 'containers/Findings/Alerts/Components/CreateTicket';
import { useBlueprintsMapping } from 'containers/SharedState/hooks/useBlueprintsMapping';

interface Props {}

// depends on ActiveResourceProvider
export const AlertDetailsTable: FC<Props> = () => {
  const { alerts, alertStatus, alertBlueprints } = useSelector(
    selectActiveResource,
  );
  const { getAlerts, getAlertBlueprints } = useActiveResourceActions();
  const { getAlertListStatus } = useAlertStatus();
  const onCreateTicket = alert => {
    getAlertListStatus([alert.uuid!]);
  };

  const ticketModal = useDisclosure();
  const notificationModal = useDisclosure();
  const excludePoliciesDisclosure = useDisclosure();
  const changeSeverityDisclosure = useDisclosure();
  const remediate = useDisclosure();
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});
  const dispatch = useDispatch();
  const [selectedPolicies, setSelectedPolicies] = useState<string[]>([]);
  const [allPolicies, setAllPolicies] = useState<Record<string, AlertResponse>>(
    {},
  );
  const [blueprintOptions, setBlueprintsOptions] = useState<
    Record<string, any>[]
  >([]);
  const [currentBlueprint, setCurrentBlueprint] = useState<Record<string, any>>(
    {},
  );
  const [changeSeverity, setChangeSeverity] = useState(false);
  const { getOptions: getBlueprintOptions } = useBlueprintsMapping();

  useEffect(() => {
    getAlertBlueprints();
  }, [getAlertBlueprints]);

  useEffect(() => {
    let items: Record<string, AlertResponse> = {};
    each(alerts.data, o => (items[o.uuid!] = o));
    setAllPolicies(items);
  }, [alerts.data]);

  //set blueprint options
  useEffect(() => {
    setBlueprintsOptions(getBlueprintOptions(alertBlueprints.data));
  }, [getBlueprintOptions, alertBlueprints.data]);

  useEffect(() => {
    setCurrentBlueprint(blueprintOptions[0]);
  }, [blueprintOptions]);

  // fetch alerts
  useEffect(() => {
    getAlerts(currentBlueprint?.value);
  }, [getAlerts, currentBlueprint]);

  const renderActions = ({ row }) => {
    const tickets = filter(
      alertStatus.data?.[row?.uuid],
      o => o.action === Clients.Jira || o.action === Clients.Servicenow,
    );

    const menuItems = [
      {
        label: 'Create ticket',
        icon: <TicketIcon />,
        onClick: () => {
          setCurrentRecord(row);
          ticketModal.onOpen();
        },
        isDisabled: !isEmpty(tickets),
      },
      {
        label: 'Send notification',
        icon: <NotifyIcon />,
        onClick: () => {
          setCurrentRecord(row);
          notificationModal.onOpen();
        },
      },
      {
        label: 'Add exception',
        icon: <ComplianceCheckPassedIcon />,
        onClick: () => {
          setSelectedPolicies([row?.uuid]);
          excludePoliciesDisclosure.onOpen();
        },
      },
      {
        label: 'Change severity',
        icon: <SeverityIcon />,
        onClick: () => {
          setCurrentRecord(row);
          changeSeverityDisclosure.onOpen();
          setChangeSeverity(true);
        },
      },
      {
        label: 'Remediate',
        icon: <RemediateIcon />,
        onClick: () => {
          setCurrentRecord(row);
          remediate.onOpen();
        },
      },
    ];

    return (
      <Box onClick={e => e.stopPropagation()}>
        <TableActionsMenu menuItems={menuItems} />
      </Box>
    );
  };

  const columns = [
    {
      header: (
        <HStack>
          <Checkbox
            isChecked={
              !!selectedPolicies?.length &&
              selectedPolicies?.length === alerts.data?.length
            }
            isDisabled={alerts.isLoading}
            onChange={e => {
              const values = e.target.checked
                ? map(alerts.data, o => o.uuid!)
                : [];
              setSelectedPolicies(values);
            }}
            isIndeterminate={
              !!selectedPolicies?.length &&
              selectedPolicies?.length !== alerts.data?.length
            }
          />
          <Box pl={12}>Issues</Box>
        </HStack>
      ),
      accessor: 'message',
      align: 'left',
      render: ({ value, row }) => {
        const isChecked = includes(selectedPolicies, row?.uuid!);
        return (
          <HStack>
            <Checkbox
              isChecked={isChecked}
              key={`${row.uuid}-`}
              onChange={e => {
                e.stopPropagation();
                // Toggle: add when unchecked, remove when checked (the branches
                // were inverted, so the checkbox never changed the selection).
                let values: string[] = [];
                if (!isChecked) {
                  let items = new Set<string>(selectedPolicies);
                  items.add(row.uuid);
                  values = toArray(items) as string[];
                } else values = filter(selectedPolicies, o => o !== row.uuid!);
                setSelectedPolicies(values);
              }}
            />
            <Box pl={8}>
              <WithResourceIcon resourceType={IconTypes.Policy}>
                <Text whiteSpace="wrap" noOfLines={2}>
                  {value}
                </Text>
              </WithResourceIcon>
            </Box>
          </HStack>
        );
      },
    },
    {
      header: 'Category',
      accessor: 'alert_category',
      render: renderAlertCategory,
      align: 'left',
      styles: { width: '200px', cell: { maxWidth: '200px' } },
    },
    {
      header: 'Severity',
      accessor: 'severity',
      render: ({ value }) => renderSeverityBar({ value, isInline: false }),
      align: 'left',
      styles: { width: 100 },
    },
    {
      header: 'Timestamp',
      accessor: 'created_at',
      render: renderTime,
      align: 'left',
      styles: { width: 100 },
    },
    {
      header: renderAlertsStatusHeader(),
      render: ({ row }) => {
        const tickets = alertStatus?.data?.[row?.uuid];
        return renderAlertsStatus(tickets);
      },
      align: 'left',
      styles: { width: 100 },
    },
    {
      header: 'Actions',
      styles: { width: '100px', cell: { maxWidth: '100px' } },
      render: renderActions,
    },
  ];

  return (
    <Stack h="full" spacing={4}>
      <HStack justify="space-between" w="full">
        <Box w={64}>
          <Select
            options={blueprintOptions}
            value={currentBlueprint}
            onChange={setCurrentBlueprint}
            menuPortalTarget={document.body}
            showIconInValueContainer
          />
        </Box>
        <PrimaryButton
          leftIcon={<ExceptionIcon />}
          isDisabled={!selectedPolicies?.length}
          onClick={() => {
            excludePoliciesDisclosure.onOpen();
          }}
        >
          Exclude Policies
        </PrimaryButton>
      </HStack>
      <Box flex={1} h="full">
        <Table
          columns={columns}
          data={alerts.data}
          isLoading={alerts.isLoading}
          styles={{ height: '33vh' }}
          onRowClick={row => {
            const values = !includes(selectedPolicies, row?.uuid!)
              ? [...selectedPolicies, row.uuid!]
              : filter(selectedPolicies, o => o !== row.uuid!);
            setSelectedPolicies(values);
          }}
          pagination={{
            pageInfo: alerts.page.info,
            totalCount: alerts.page.totalCount,
            onChange: (info: PageInfo) =>
              dispatch(actions.updateAlertsPagination(info)),
          }}
        />
        {ticketModal.isOpen && (
          <CreateTicket
            onClose={ticketModal.onClose}
            isOpen={ticketModal.isOpen}
            alert={currentRecord}
            onCreate={onCreateTicket}
            notification_for={NotificationFor.Alerts}
          />
        )}
        {notificationModal.isOpen && (
          <CreateNotification
            onClose={notificationModal.onClose}
            isOpen={notificationModal.isOpen}
            alert={currentRecord as AlertResponse}
            onCreate={onCreateTicket}
            notification_for={NotificationFor.Alerts}
          />
        )}
        {remediate.isOpen && (
          <RemediationModal
            state={remediate}
            alert={currentRecord as AlertResponse}
          />
        )}
        {excludePoliciesDisclosure.isOpen && (
          <ExcludePoliciesForResource
            isOpen={excludePoliciesDisclosure.isOpen}
            onClose={excludePoliciesDisclosure.onClose}
            selectedPolicies={map(selectedPolicies, o => allPolicies[o])}
            blueprintId={currentBlueprint?.value}
            onSuccess={() => getAlerts(currentBlueprint?.value)}
            totalPolicies={alerts.page.totalCount}
          />
        )}
        {changeSeverityDisclosure.isOpen && (
          <AddException
            alert={currentRecord as AlertResponse}
            onClose={() => {
              changeSeverityDisclosure.onClose();
              setChangeSeverity(false);
            }}
            isOpen={changeSeverityDisclosure.isOpen}
            onSuccess={() => getAlerts(currentBlueprint?.value)}
            exceptionFor="Issue"
            changeSeverity={changeSeverity}
          />
        )}
      </Box>
    </Stack>
  );
};
